// src/pages/AdminDashboard.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate }                        from "react-router-dom";
import { useTranslation }                     from "react-i18next";
import { ref, onValue, update, off }          from "firebase/database";
import { db }                                 from "../firebase/config";
import { useAuth }                            from "../context/AuthContext";
import { useAdmin }                           from "../context/AdminContext";
import { MODERATOR_UIDS }                     from "../constants/roles";

// ─── Stil sabitleri ────────────────────────────────────────────────────────────
const S = {
  gold:      "#d4a853",
  goldDim:   "var(--bone-text-muted)",
  goldFaint: "rgba(212,168,83,0.08)",
  goldGlow:  "rgba(212,168,83,0.12)",
  bg:        "#0d0a05",
  card:      "#12100a",
  row:       "#17110a",
  rowHov:    "#1e160c",
  bright:    "#f0e6cc",
  dim:       "rgba(232,213,176,0.45)",
  red:       "#e05c5c",
  redDim:    "rgba(224,92,92,0.15)",
  green:     "#6ec97a",
  mono:      "'Courier Prime', monospace",
  serif:     "'Playfair Display', serif",
};

function Divider() {
  return <div style={{ height: 1, background: `linear-gradient(90deg,transparent,${S.goldDim},transparent)`, margin: "0" }} />;
}

function ActionBtn({ onClick, danger = false, gold = false, children }) {
  const [hov, setHov] = useState(false);
  const borderColor = danger
    ? (hov ? "rgba(224,92,92,0.75)" : "rgba(224,92,92,0.35)")
    : gold
    ? (hov ? "rgba(212,168,83,0.75)" : S.goldDim)
    : (hov ? "rgba(212,168,83,0.45)" : "rgba(212,168,83,0.18)");
  const textColor = danger
    ? (hov ? S.red  : "rgba(224,92,92,0.65)")
    : gold
    ? (hov ? S.gold : S.goldDim)
    : (hov ? S.dim  : "rgba(212,168,83,0.3)");

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding: "5px 13px", fontSize: 10, fontFamily: S.mono,
        letterSpacing: "0.14em", textTransform: "uppercase",
        cursor: "pointer", border: `1px solid ${borderColor}`,
        borderRadius: 6,
        background: hov ? (danger ? S.redDim : S.goldGlow) : "transparent",
        color: textColor, transition: "all 0.2s ease",
      }}
    >
      {children}
    </button>
  );
}

function UserRow({ u, onToggleBlock, onPromote, t }) {
  const [hov, setHov] = useState(false);
  const isSelf = MODERATOR_UIDS.includes(u.id);
  const isAdmin = u.role === "admin" || u.isAdmin;

  return (
    <tr
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        borderBottom: `1px solid ${S.goldFaint}`,
        background: hov ? S.rowHov : S.row,
        transition: "background 0.2s ease",
      }}
    >
      {/* Kullanıcı */}
      <td style={{ padding: "14px 16px" }}>
        <div style={{ fontFamily: S.mono, fontSize: 13, color: S.bright }}>
          {u.displayName || "—"}
          {isSelf && (
            <span style={{ marginLeft: 8, fontSize: 9, color: S.gold,
              letterSpacing: "0.18em", textTransform: "uppercase" }}>
              ✦ {t('admin.you')}
            </span>
          )}
        </div>
        <div style={{ fontFamily: S.mono, fontSize: 10, color: S.dim, marginTop: 3 }}>
          {u.id}
        </div>
      </td>

      {/* E-posta */}
      <td style={{ padding: "14px 16px", fontFamily: S.mono, fontSize: 12, color: S.dim }}>
        {u.email || "—"}
      </td>

      {/* Rol */}
      <td style={{ padding: "14px 16px" }}>
        {isAdmin ? (
          <span style={{ fontFamily: S.mono, fontSize: 10, color: S.gold,
            letterSpacing: "0.16em", textTransform: "uppercase" }}>✦ Admin</span>
        ) : (
          <span style={{ fontFamily: S.mono, fontSize: 10, color: S.dim,
            letterSpacing: "0.12em", textTransform: "uppercase" }}>{t('admin.user')}</span>
        )}
      </td>

      {/* Durum */}
      <td style={{ padding: "14px 16px" }}>
        <span style={{
          fontFamily: S.mono, fontSize: 10, letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: u.isBanned ? S.red : S.green,
        }}>
          {u.isBanned ? `● ${t('admin.blocked')}` : `● ${t('admin.active')}`}
        </span>
      </td>

      {/* Aksiyonlar */}
      <td style={{ padding: "14px 16px", textAlign: "right" }}>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
          {!isSelf && (
            <ActionBtn onClick={() => onPromote(u.id, u.role, u.isAdmin)} gold={!isAdmin}>
              {isAdmin ? t('admin.removeAdmin') : t('admin.makeAdmin')}
            </ActionBtn>
          )}
          {!isSelf && (
            <ActionBtn onClick={() => onToggleBlock(u.id, u.isBanned)} danger={!u.isBanned}>
              {u.isBanned ? t('admin.unblock') : t('admin.block')}
            </ActionBtn>
          )}
          {isSelf && (
            <span style={{ fontFamily: S.mono, fontSize: 9, color: S.goldDim,
              letterSpacing: "0.14em" }}>
              {t('admin.rootAdmin')}
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}

function PermissionDenied({ errorCode, t }) {
  return (
    <div style={{
      margin: "52px 0", padding: "36px 32px",
      border: "1px solid rgba(224,92,92,0.3)",
      background: S.redDim,
    }}>
      <p style={{ fontFamily: S.mono, fontSize: 9, letterSpacing: "0.32em",
        color: "rgba(224,92,92,0.55)", textTransform: "uppercase", marginBottom: 12 }}>
        ✦ {t('admin.accessDenied')} ✦
      </p>
      <h2 style={{ fontFamily: S.serif, color: S.red, fontSize: 20,
        fontWeight: 700, marginBottom: 14 }}>
        {t('admin.accessDeniedTitle')}
      </h2>
      <p style={{ fontFamily: S.mono, fontSize: 12,
        color: "rgba(232,213,176,0.55)", lineHeight: 1.85, marginBottom: 20 }}>
        <code style={{ color: S.gold }}>/users</code> {t('admin.noReadAccess')}{" "}
        Firebase Console →{" "}
        <strong style={{ color: S.bright }}>Realtime Database → Rules</strong>{" "}
        {t('admin.addRule')}:
      </p>
      <pre style={{
        background: "#0a0803", border: `1px solid ${S.goldFaint}`,
        padding: "16px 20px", fontFamily: S.mono, fontSize: 11,
        color: "rgba(212,168,83,0.65)", lineHeight: 1.7,
        overflowX: "auto", marginBottom: 16,
      }}>
{`{
  "rules": {
    "users": {
      ".read": "auth != null && auth.uid === '${MODERATOR_UIDS[0]}'",
      "$uid": {
        ".read": "auth != null && (auth.uid === $uid || auth.uid === '${MODERATOR_UIDS[0]}')",
        ".write": "auth != null && (auth.uid === $uid || auth.uid === '${MODERATOR_UIDS[0]}')"
      }
    },
    "usernames": {
      ".read": "true",
      ".write": "auth != null"
    }
  }
}`}
      </pre>
      {errorCode && (
        <p style={{ fontFamily: S.mono, fontSize: 10,
          color: "rgba(224,92,92,0.45)", letterSpacing: "0.1em" }}>
          {t('admin.errorCode')}: <code style={{ color: S.red }}>{errorCode}</code>
        </p>
      )}
    </div>
  );
}

// ─── ANA BİLEŞEN ──────────────────────────────────────────────────────────────
export default function AdminDashboard() {
  const navigate        = useNavigate();
  const { user }        = useAuth();
  const { isAdmin }     = useAdmin();
  const { t }           = useTranslation();

  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [dbError, setDbError] = useState(null);
  const [toast,   setToast]   = useState("");

  const listenerRef = useRef(null);

  useEffect(() => {
    if (!user) { navigate("/", { replace: true }); return; }
    const confirmed = isAdmin || MODERATOR_UIDS.includes(user?.uid);
    if (!confirmed) { navigate("/", { replace: true }); }
  }, [user, isAdmin, navigate]);

  useEffect(() => {
    if (!user) return;

    setLoading(true);
    setDbError(null);
    setUsers([]);
    if (!MODERATOR_UIDS.includes(user.uid)) {
      setDbError("PERMISSION_DENIED");
      setLoading(false);
      return;
    }

    const usersRef = ref(db, "users");
    listenerRef.current = usersRef;

    onValue(
      usersRef,
      (snap) => {
        if (snap.exists()) {
          const list = Object.entries(snap.val()).map(([uid, val]) => ({
            id:       uid,
            ...val,
            isBanned: val.isBanned === true || val.status === "blocked",
            status:   val.status || (val.isBanned ? "blocked" : "active"),
          }));
          list.sort((a, b) => {
            if ((a.isAdmin || a.role === "admin") && !(b.isAdmin || b.role === "admin")) return -1;
            if (!(a.isAdmin || a.role === "admin") && (b.isAdmin || b.role === "admin")) return 1;
            return (a.displayName || "").localeCompare(b.displayName || "", "tr");
          });
          setUsers(list);
        } else {
          setUsers([]);
        }
        setDbError(null);
        setLoading(false);
      },
      (error) => {
        const isModerator = MODERATOR_UIDS.includes(user.uid);
        setUsers([]);
        setDbError(isModerator ? error.code || "UNKNOWN_ERROR" : "PERMISSION_DENIED");
        setLoading(false);
        if (!isModerator) navigate("/", { replace: true });
      }
    );

    return () => {
      if (listenerRef.current) {
        off(listenerRef.current);
        listenerRef.current = null;
      }
    };
  }, [user, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleToggleBlock(uid, currentIsBanned) {
    if (!MODERATOR_UIDS.includes(user?.uid)) return;
    const newBanned = !currentIsBanned;
    const confirmKey = newBanned ? 'admin.confirm.block' : 'admin.confirm.unblock';
    if (!window.confirm(t(confirmKey))) return;
    try {
      await update(ref(db, `users/${uid}`), {
        isBanned: newBanned,
        status:   newBanned ? "blocked" : "active",
      });
      showToast(newBanned ? t('admin.toast.blocked') : t('admin.toast.unblocked'));
    } catch (err) {
      showToast(t('admin.toast.error') + ": " + err.message, true);
    }
  }

  async function handlePromote(uid, currentRole, currentIsAdmin) {
    if (!MODERATOR_UIDS.includes(user?.uid)) return;
    const makeAdmin = !(currentRole === "admin" || currentIsAdmin);
    const confirmKey = makeAdmin ? 'admin.confirm.makeAdmin' : 'admin.confirm.removeAdmin';
    if (!window.confirm(t(confirmKey))) return;
    try {
      await update(ref(db, `users/${uid}`), {
        role:    makeAdmin ? "admin" : "user",
        isAdmin: makeAdmin,
      });
      showToast(makeAdmin ? t('admin.toast.promoted') : t('admin.toast.demoted'));
    } catch (err) {
      showToast(t('admin.toast.error') + ": " + err.message, true);
    }
  }

  function showToast(msg, isErr = false) {
    setToast({ msg, isErr });
    setTimeout(() => setToast(""), 3000);
  }

  const totalUsers  = users.length;
  const totalAdmins = users.filter(u => u.isAdmin || u.role === "admin").length;
  const totalBanned = users.filter(u => u.isBanned).length;

  return (
    <div style={{
      minHeight: "100vh", background: S.bg,
      padding: "90px 48px 80px",
      fontFamily: S.mono, color: S.bright,
    }}>

      {/* ← Geri */}
      <button onClick={() => navigate("/")} style={{
        position: "fixed", top: 24, left: 28, background: "none",
        border: "none", color: "var(--bone-text-accent)", cursor: "pointer",
        fontFamily: S.mono, fontSize: 11, letterSpacing: "0.2em", zIndex: 100,
      }}>
        ← {t('admin.backToHome')}
      </button>

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 32, right: 32,
          padding: "12px 20px", zIndex: 9999,
          background: toast.isErr ? S.redDim : "rgba(110,201,122,0.12)",
          border: `1px solid ${toast.isErr ? "rgba(224,92,92,0.4)" : "rgba(110,201,122,0.35)"}`,
          fontFamily: S.mono, fontSize: 11, letterSpacing: "0.1em",
          color: toast.isErr ? S.red : S.green,
        }}>
          {toast.msg}
        </div>
      )}

      {/* Başlık */}
      <p style={{ fontSize: 9, letterSpacing: "0.38em", color: S.goldDim,
        textTransform: "uppercase", marginBottom: 10 }}>
        ✦ BoneCast Admin ✦
      </p>
      <h1 style={{ fontFamily: S.serif, fontSize: "clamp(28px,4vw,46px)",
        fontWeight: 900, color: S.gold, margin: "0 0 40px", lineHeight: 1.1 }}>
        {t('admin.title')}
      </h1>

      {loading && (
        <p style={{ color: S.goldDim, letterSpacing: "0.2em", marginTop: 40 }}>
          ✦ {t('common.loading')}
        </p>
      )}

      {!loading && dbError && <PermissionDenied errorCode={dbError} t={t} />}

      {!loading && !dbError && (
        <>
          {/* İstatistik kartları */}
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 40 }}>
            {[
              { label: t('admin.stats.total'),   val: totalUsers   },
              { label: t('admin.stats.admins'),  val: totalAdmins, accent: S.gold },
              { label: t('admin.stats.blocked'), val: totalBanned, accent: totalBanned > 0 ? S.red : S.dim },
            ].map(({ label, val, accent }) => (
              <div key={label} style={{
                flex: "1 1 140px", padding: "20px 24px",
                background: S.card, border: `1px solid ${S.goldFaint}`,
              }}>
                <div style={{ fontFamily: S.serif, fontSize: 32, fontWeight: 700,
                  color: accent || S.bright, lineHeight: 1 }}>
                  {val}
                </div>
                <div style={{ fontSize: 9, letterSpacing: "0.24em",
                  textTransform: "uppercase", color: S.dim, marginTop: 6 }}>
                  {label}
                </div>
              </div>
            ))}
          </div>

          <Divider />

          {users.length === 0 ? (
            <p style={{ color: S.goldDim, letterSpacing: "0.18em", marginTop: 40 }}>
              {t('admin.noUsers')}
            </p>
          ) : (
            <div style={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              <table style={{ width: "100%", minWidth: 760, marginTop: 0, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid rgba(212,168,83,0.22)` }}>
                    {[
                      t('admin.col.user'),
                      t('admin.col.email'),
                      t('admin.col.role'),
                      t('admin.col.status'),
                      "",
                    ].map((h, i) => (
                      <th key={i} style={{
                        padding: "14px 16px", textAlign: h === "" ? "right" : "left",
                        fontSize: 9, letterSpacing: "0.28em", textTransform: "uppercase",
                        color: S.goldDim, fontWeight: 400, fontFamily: S.mono,
                      }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <UserRow
                      key={u.id}
                      u={u}
                      onToggleBlock={handleToggleBlock}
                      onPromote={handlePromote}
                      t={t}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
